import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { NgbAccordionDirective, NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Backend } from 'src/app/backend-interface/backend';
import { LoadingErrorComponent } from 'src/app/components/loading-error/loading-error.component';
import { ToastComponent } from 'src/app/components/toast/toast.component';
import { RefPolicy, RequestRefPolicy } from 'src/app/service/global.service';
import { ToastService } from 'src/app/service/toast.service';
import { TranslationService } from 'src/app/service/translation.service';
import { FormRefPoliciesSettingsComponent } from './form-ref-policies-settings/form-ref-policies-settings.component';

@Component({
  selector: 'app-ref-policies',
  templateUrl: './ref-policies.component.html',
  styleUrls: ['./ref-policies.component.scss'],
  standalone: true,
  imports: [
    NgbAccordionModule, 
    TranslateModule, 
    ToastComponent, 
    LoadingErrorComponent, 
    FormRefPoliciesSettingsComponent,
    NgbAccordionDirective
  ],
  providers: [ToastService, TranslationService]
})
export class RefPoliciesComponent implements OnInit {

  readonly listAllRefPolicies = signal<RefPolicy[]>([]);
  readonly listClientRefPolicies = signal<RefPolicy[]>([]);
  readonly listDefaultRefPolicies = signal<RefPolicy[]>([]);
  readonly loadingSDS = signal(false);

  readonly toastService = inject(ToastService);
  readonly translationService = inject(TranslationService);

  ngOnInit(): void {
    this.loadRefPolicies();
  }

  private async loadRefPolicies(): Promise<void> {
   
    const all = await Backend.REF_POLICIES.getRefPolicies();
    this.listAllRefPolicies.set(all);
    this.listClientRefPolicies.set(Backend.REF_POLICIES.filterListRefPolicies(all, 'client') ?? []);
    this.listDefaultRefPolicies.set(Backend.REF_POLICIES.filterListRefPolicies(all, 'default') ?? []);
  }

  fixPanel(event: any): void {
    // Empêche la fermeture des panels 'clients' et 'default'
    if (
      (event.panelId === 'clients' || event.panelId === 'default') &&
      event.nextState === false
    ) {
      event.preventDefault();
    }
  }

  async addRefPolicy(refPolicy: RequestRefPolicy): Promise<void> {
    try {
      await Backend.REF_POLICIES.addRefPolicy(refPolicy);
      await this.loadRefPolicies();
      this.translationService.get('security-policies-toast-add').subscribe((msg) => {
        this.toastService.show(
          `${refPolicy.name} ${msg}`,
          { classname: 'bg-success', delay: 5000 }
        );
      });
    } catch (e) {
      const err = e as HttpErrorResponse;
      this.toastService.show(err.error?.message ?? 'Error', { classname: 'bg-danger', delay: 5000 });
    }
  }

  async deleteRefPolicy(refPolicy: RefPolicy): Promise<void> {
    try {
      await Backend.REF_POLICIES.deleteRefPolicy(refPolicy);
      await this.loadRefPolicies();
      this.translationService.get('security-policies-toast-delete').subscribe((msg) => {
        this.toastService.show(
          `${refPolicy.name} ${msg}`,
          { classname: 'bg-success', delay: 5000 }
        );
      });
    } catch (e) {
      const err = e as HttpErrorResponse;
      this.toastService.show(err.error?.message ?? 'Error', { classname: 'bg-danger', delay: 5000 });
    }
  }

  async patchRefPolicy(refPolicy: RequestRefPolicy): Promise<void> {
    try {
      await Backend.REF_POLICIES.patchRefPolicy(refPolicy);
      await this.loadRefPolicies();
      this.translationService.get('ref-policies-edit-success').subscribe((msg) => {
        this.toastService.show(
          `${refPolicy.name} ${msg}`,
          { classname: 'bg-success', delay: 5000 }
        );
      });
    } catch (e) {
      const err = e as HttpErrorResponse;
      this.toastService.show(err.error?.message ?? 'Error', { classname: 'bg-danger', delay: 5000 });
    }
  }

  async synchroSDS(): Promise<void> {
    try {
      this.loadingSDS.set(true);
      await Backend.REF_POLICIES.synchroSDS();
      this.loadingSDS.set(false);
      this.translationService.get('ref-policies-synchro-success').subscribe((msg) => {
        this.toastService.show(
          msg,
          { classname: 'bg-success', delay: 5000 }
        );
      });
      await this.loadRefPolicies();
    } catch (e) {
      this.loadingSDS.set(false);
      const err = e as HttpErrorResponse;
      this.toastService.show(err.error?.message ?? 'Error', { classname: 'bg-danger', delay: 5000 });
    }
  }
}
